import React, { useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Typography, Button } from "@mui/material/";
import { strings } from "../../common/i18n";
import { helpers } from "../../common/lib";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import actions from "../../Store/actions";
import { v4 as uuidv4 } from "uuid";
import { logToSentry } from "../../config/Sentry/";
import styles from "../styles";
/**
 *  Hook for user table data and operations
 */

export default () => {
    const { userOrganisations, branches } = useSelector((state) => state);
    const dispatch = useDispatch();
    const [optionOpen, setOptionsOpen] = useState(null);
    const [error, setError] = useState(false);
    const branchNameRef = useRef({});

    /** On menu open close */
    const onMenuClose = () => setOptionsOpen(null);
    const onMenuOpen = (event) => setOptionsOpen(event.currentTarget);

    /** On datamap */
    const onDataMap = ({ branchRef, id, name }) => ({
        id,
        name,
        branchRef: helpers.nameJoin(branchRef)
    });

    /** On mapping with csv  */
    const onCsvMap = useCallback(({ branchRef, name, row }) => {
        const next = { branchRef: [], errors: [], name };

        if (branchRef !== null) {
            next.branchRef = helpers.nameSplit(branchRef);
        }
        return next;
    }, []);

    /**  On Csv upload*/
    const onCsvUpload = () => {
        branchNameRef.current = {};
        setError(false);
    };

    /** On adding the  branch */
    const onDataItemAdd = () => dispatch(actions.branches.create.itemAdd());

    /** On clearing the selected branch */
    const onDataItemClear = () => dispatch(actions.branches.create.itemClear());

    /** On selecting the single branch */
    const onDataItemSet = (item) =>
        dispatch(actions.branches.create.itemSet(item));

    /** On Save  */
    const onDataSave = async (data) => {
        try {
            await dispatch(actions.branches.create.postRequest(data));
            await dispatch(
                actions.branches.create.getRequest(userOrganisations.item.id)
            );
            dispatch(
                actions.toast.create.success(
                    strings("messages.addBranchSuccess")
                )
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On branch data save:", { error: errStr });
            dispatch(
                actions.toast.create.error(strings("errors.onDataSaveBranch"))
            );
        }
    };

    /** On editing columns */
    const onEdit = (row) => {
        if (row.id) onDataItemSet(row);
    };

    /** Rows data */
    const data = useMemo(() => {
        branches.data.map((item) => {
            const id = uuidv4();
            // When creating new data generate random id for table
            if (!item.id) item.id = id;

            item.branchReference = helpers.nameJoin(item.branchRef);
            return item;
        });

        return branches.data;
    }, [branches.data]);

    /** Columns */
    const columns = useMemo(
        () => [
            {
                id: "name",
                header: strings("name"),
                accessorKey: "name",
                hideable: false,
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "branchReference",
                header: strings("branchRef"),
                accessorKey: "branchReference",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            }
        ],
        []
    );

    /** Row Actions */
    const rowActions = (row) => {
        const { name } = row.original;
        return [
            name && (
                <IconButton
                    key="edit"
                    title={strings("edit")}
                    onClick={() => onEdit(row.original)}
                >
                    <EditIcon color="primary" />
                </IconButton>
            )
        ];
    };

    /** Toolbar Components */
    const toolBarComponents = () => (
        <>
            <IconButton
                key="edit"
                title={strings("options")}
                onClick={onMenuOpen}
            >
                <MoreHorizIcon color="primary" />
            </IconButton>
            <Button
                sx={styles.primaryButton}
                variant="contained"
                size="small"
                color="primary"
                onClick={onDataItemAdd}
                endIcon={<AddIcon />}
            >
                {strings("addBranch")}
            </Button>
        </>
    );

    return {
        columns,
        data,
        error,
        onCsvMap,
        onCsvUpload,
        onDataMap,
        onDataItemAdd,
        onDataItemClear,
        onDataItemSet,
        onDataSave,
        optionOpen,
        onMenuOpen,
        onMenuClose,
        rowActions,
        toolBarComponents
    };
};
